export interface AppVersionManifest {
  buildId: string;
  builtAt: string;
}

export type AppVersionFetcher = (signal?: AbortSignal) => Promise<AppVersionManifest | null>;

export const APP_VERSION_CHECK_INTERVAL_MS = 120_000;
export const DISMISSED_APP_BUILD_STORAGE_KEY = "sbotLabs.dismissedAppBuildId";
const PRELOAD_RELOAD_STORAGE_PREFIX = "sbotLabs.preloadReloaded";

export const APP_BUILD_ID =
  typeof __APP_BUILD_ID__ === "string" ? __APP_BUILD_ID__ : "development";

export async function fetchAppVersion(signal?: AbortSignal): Promise<AppVersionManifest | null> {
  try {
    const response = await fetch("/version.json", {
      cache: "no-store",
      signal,
    });
    if (!response.ok) return null;

    const value: unknown = await response.json();
    if (!isAppVersionManifest(value)) return null;
    return value;
  } catch {
    return null;
  }
}

export function isAppVersionManifest(value: unknown): value is AppVersionManifest {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AppVersionManifest>;
  return (
    typeof candidate.buildId === "string" &&
    candidate.buildId.trim().length > 0 &&
    typeof candidate.builtAt === "string" &&
    candidate.builtAt.trim().length > 0
  );
}

export function isBroadcastOutputPath(pathname: string): boolean {
  return /^\/(?:overlay|league-overlay)\//.test(pathname);
}

interface PreloadErrorRecoveryOptions {
  currentBuildId?: string;
  reload?: () => void;
  storage?: Pick<Storage, "getItem" | "setItem">;
  target?: Pick<Window, "addEventListener" | "removeEventListener">;
}

export function installPreloadErrorRecovery({
  currentBuildId = APP_BUILD_ID,
  reload = () => window.location.reload(),
  storage = window.sessionStorage,
  target = window,
}: PreloadErrorRecoveryOptions = {}): () => void {
  const storageKey = `${PRELOAD_RELOAD_STORAGE_PREFIX}:${currentBuildId}`;
  let reloadStarted = false;

  const handlePreloadError = (event: Event) => {
    if (reloadStarted) return;

    try {
      if (storage.getItem(storageKey) === "1") return;
      storage.setItem(storageKey, "1");
    } catch {
      // Without durable per-tab storage, an automatic reload could loop.
      return;
    }

    event.preventDefault();
    reloadStarted = true;
    reload();
  };

  target.addEventListener("vite:preloadError", handlePreloadError);
  return () => target.removeEventListener("vite:preloadError", handlePreloadError);
}
